<template>

    <!-- <header v-if="!isInitializing">
      <div class="h-header border-b border-gray-300 ">
          <ul class="flex w-full h-header justify-between">
              <li 
                v-for="view, i in VIEWS"
                @click="activeView = view"
                :key="view" 
                :class="[
                    (activeView == view) ? 'border-b-4 border-gray-500' : '',
                    
                    'w-full flex items-center justify-center'
                ]">
                <a href="#"   
                class="cursor-pointer px-4 py-1 text-xs sm:text-sm font-base leading-4 text-gray-700 h-full w-full flex items-center justify-center"
                :class="(i == 0) ? '' : 'border-l border-gray-300 border-dashed'"
                >
                  <span>
                      {{ view }}
                  </span>
                </a>
              </li>
          </ul>
      </div>
    </header> -->

    <main v-if="!isInitializing" class="overflow-auto h-full p-4 scrollbar-hide">
      <!-- Tabs: Account | CAAP review activity (ENG-16) -->
      <div class="mb-4 border-b border-gray-300 flex gap-1">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
          :class="activeTab === 'account'
            ? 'border-gray-800 text-gray-900'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="activeTab = 'account'"
        >
          Account
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
          :class="activeTab === 'activity'
            ? 'border-gray-800 text-gray-900'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="activeTab = 'activity'"
        >
          CAAP review activity
        </button>
      </div>

      <!-- ========== CAAP review activity tab (ENG-16) ========== -->
      <div
        v-if="activeTab === 'activity'"
        :class="[
          (isWaiting) ? 'pointer-events-none cursor-wait opacity-25' : '',
          'border border-gray-300 rounded-md px-6 py-4 text-xs space-y-8'
        ]"
      >
        <div v-if="isLoadingReviewActivity" class="py-8 text-center text-gray-500">
          Loading review activity…
        </div>

        <template v-else>
          <!-- Mock exam statistics (user.subjects) -->
          <section>
            <h2 class="font-bold text-sm mb-3">Mock exam statistics</h2>
            <p v-if="!user.subjects || user.subjects.length === 0" class="text-gray-500 italic">
              No mock exam statistics on this user.
            </p>
            <ul v-else class="space-y-4">
              <li
                v-for="subject in user.subjects"
                :key="subject.id"
                class="border border-gray-200 rounded-md p-3 bg-gray-50"
              >
                <p class="font-semibold text-sm text-gray-900">{{ formatSlugLabel(subject.id) }}</p>
                <ul class="mt-2 ml-4 list-disc space-y-1 text-gray-700">
                  <li>
                    <span class="font-semibold">Best time:</span>
                    {{ moment.utc(subject.time * 1000).format('HH:mm:ss') }} ({{ subject.time }}s)
                  </li>
                  <li>
                    <span class="font-semibold">Best score:</span> {{ formatScore(subject.score) }}%
                  </li>
                  <li>
                    <span class="font-semibold">Milestones:</span>
                    <span v-if="subject.milestones && subject.milestones.length">
                      {{ subject.milestones.join(', ') }}
                    </span>
                    <span v-else class="text-gray-400">—</span>
                  </li>
                </ul>
              </li>
            </ul>
          </section>

          <!-- Global last 3 practices -->
          <section>
            <h2 class="font-bold text-sm mb-3">Last 3 practices (any subject)</h2>
            <p v-if="lastPractices.length === 0" class="text-gray-500 italic">No practices found.</p>
            <ol v-else class="list-decimal ml-5 space-y-2">
              <li v-for="practice in lastPractices" :key="practice.id">
                <span class="font-semibold">{{ practice.subjectLabel }}</span>
                <span class="text-gray-500"> · {{ practice.subtopicLabel }}</span>
                <span> · {{ practice.dateLabel }}</span>
                <span v-if="practice.score != null"> · {{ formatScore(practice.score) }}%</span>
                <span v-if="practice.result"> · {{ practice.result }}</span>
                <span v-if="practice.status" class="text-gray-400"> · {{ practice.status }}</span>
              </li>
            </ol>
          </section>

          <!-- Global last 3 mocks -->
          <section>
            <h2 class="font-bold text-sm mb-3">Last 3 mock exams (any subject)</h2>
            <p v-if="lastExams.length === 0" class="text-gray-500 italic">No mock exams found.</p>
            <ol v-else class="list-decimal ml-5 space-y-2">
              <li v-for="exam in lastExams" :key="exam.id">
                <span class="font-semibold">{{ exam.subjectLabel }}</span>
                <span> · {{ exam.dateLabel }}</span>
                <span v-if="exam.score != null"> · {{ formatScore(exam.score) }}%</span>
                <span v-if="exam.result"> · {{ exam.result }}</span>
                <span v-if="exam.status" class="text-gray-400"> · {{ exam.status }}</span>
              </li>
            </ol>
          </section>

          <!-- Per-subject accordion -->
          <section>
            <h2 class="font-bold text-sm mb-3">Last 3 practices and mock exams (by subject)</h2>
            <p v-if="subjectsWithActivity.length === 0" class="text-gray-500 italic">
              No practice or mock activity grouped by subject.
            </p>
            <div v-else class="space-y-2">
              <div
                v-for="group in subjectsWithActivity"
                :key="group.subjectId"
                class="border border-gray-200 rounded-md overflow-hidden"
              >
                <button
                  type="button"
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-left"
                  @click="toggleSubjectPanel(group.subjectId)"
                >
                  <span class="font-semibold text-sm min-w-0 truncate">{{ group.subjectLabel }}</span>
                  <span class="text-gray-600 flex-shrink-0 whitespace-nowrap">
                    Practices: {{ group.practices.length }} · Mocks: {{ group.mocks.length }}
                    {{ expandedSubjects[group.subjectId] ? '▾' : '▸' }}
                  </span>
                </button>
                <div v-if="expandedSubjects[group.subjectId]" class="px-4 py-3 space-y-4 bg-white">
                  <div>
                    <h3 class="font-semibold mb-1">Practices (up to 3)</h3>
                    <p v-if="group.practices.length === 0" class="text-gray-400 italic ml-2">None</p>
                    <ol v-else class="list-decimal ml-5 space-y-1">
                      <li v-for="practice in group.practices" :key="practice.id">
                        <span class="text-gray-700">{{ practice.subtopicLabel }}</span>
                        <span> · {{ practice.dateLabel }}</span>
                        <span v-if="practice.score != null"> · {{ formatScore(practice.score) }}%</span>
                        <span v-if="practice.result"> · {{ practice.result }}</span>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <h3 class="font-semibold mb-1">Mock exams (up to 3)</h3>
                    <p v-if="group.mocks.length === 0" class="text-gray-400 italic ml-2">None</p>
                    <ol v-else class="list-decimal ml-5 space-y-1">
                      <li v-for="exam in group.mocks" :key="exam.id">
                        <span>{{ exam.dateLabel }}</span>
                        <span v-if="exam.score != null"> · {{ formatScore(exam.score) }}%</span>
                        <span v-if="exam.result"> · {{ exam.result }}</span>
                        <span v-if="exam.status" class="text-gray-400"> · {{ exam.status }}</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>

      <!-- ========== Account tab ========== -->
      <ul v-if="activeTab === 'account'" class="flex flex-col space-y-4">
        <li 
        :class="[
          (isWaiting) ? 'pointer-events-none cursor-wait opacity-25' : '',
          'border border-gray-300 rounded-md px-8 py-4 text-xs flex flex-col divide-y divide-gray-300 ']">

          
            
          <div class="py-4">
            <h1 class="font-bold text-sm">Personal Info</h1>
            <img v-if="user.photo" class="object-cover h-10 w-10 rounded-full mr-2 my-2" :src="user.photo" alt="" />
            <div v-else class="bg-gray-100 rounded-full overflow-hidden h-10 w-10 mr-2 my-2">
              <svg
                class="w-full h-full text-gray-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z"
                ></path>
              </svg>
            </div>
            <p><strong class="font-semibold">Full Name:</strong> {{ user.firstname }} {{ user.lastname }}</p>
            <p><strong class="font-semibold">Age:</strong> {{ user.age }}</p>
            <p><strong class="font-semibold">Gender:</strong> {{ user.gender }}</p>
          </div>
          <div class="py-4">
            <h1 class="font-bold text-sm">Contact Info</h1>
            <p><strong class="font-semibold">Email:</strong> {{ user.email }}</p>
            <p><strong class="font-semibold">Phone:</strong> {{ user.phone }}</p>
            <p><strong class="font-semibold">Street:</strong> {{ user.street }}</p>
            <p><strong class="font-semibold">City:</strong> {{ user.city }}</p>
            <p><strong class="font-semibold">Region:</strong> {{ user.region }}</p>
            <p><strong class="font-semibold">Postal:</strong> {{ user.postal }}</p>
          </div>

          <div class="py-4">
            <h1 class="font-bold text-sm">Academic Info</h1>
            <p><strong class="font-semibold">School:</strong> {{ (schools.filter( e => e.id == user.school).length > 0) ? schools.filter( e => e.id == user.school)[0].name : 'No School Found'}}</p>
            <p><strong class="font-semibold">Course:</strong> {{ user.course }}</p>
            <p><strong class="font-semibold">Level:</strong> {{ user.level }}</p>
            <p><strong class="font-semibold">Batch:</strong> {{ user.batch }}</p>
          </div>

          <div v-if="schools.filter( e => e.id == user.school).length > 0" class="py-4">
            <h1 class="font-bold text-sm">School Info</h1>
            <img v-if="schools.filter( e => e.id == user.school).length > 0" class="object-cover h-10 w-10 rounded-full mr-2 my-2" :src="schools.filter( e => e.id == user.school)[0].logoUrl" alt="" />
            <div v-else class="bg-gray-100 rounded-full overflow-hidden h-10 w-10 mr-2 my-2">
              <svg
                class="w-full h-full text-gray-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z"
                ></path>
              </svg>
            </div>
            <p><strong class="font-semibold">School ID:</strong> {{ schools.filter( e => e.id == user.school)[0].id }}</p>
            <p><strong class="font-semibold">School Name:</strong> {{ schools.filter( e => e.id == user.school)[0].name }}</p>
            <p><strong class="font-semibold">School Shorthand:</strong> {{ schools.filter( e => e.id == user.school)[0].shorthand }}</p>
          </div>

          <div class="py-4">
            <h1 class="font-bold text-sm">Account Info</h1>
            <p><strong class="font-semibold">User ID:</strong> {{ user.id }}</p>
            <p><strong class="font-semibold">Skillpoints:</strong> {{ user.skillpoints }}</p>
            <p><strong class="font-semibold">Date Signed Up:</strong> {{ moment.unix(user.date_created/1000).format('LLLL') }}</p>
            <p><strong class="font-semibold">Endorsed By:</strong> {{ user.endorser }}</p>
            <p><strong class="font-semibold">Account Type:</strong> {{ user.type }}</p>
            <p v-if="user.type == 'endorser'"><strong class="font-bold">Endorser Code:</strong> {{ user.endorser_code }}</p>
            <div class="bg-gray-100 py-3 mt-4 text-xs text-black px-3">
              <code>
                {{ user.reviewers }}
              </code>
            </div>
            <br/>
            <button class="underline" @click="fetchUserClaims(user.email)">Fetch user custom claims</button>
          </div>

          <!-- CAAP Info -->
          <div class="py-4">
            <h1 class="font-bold text-sm">CAAP Info</h1>
            <p><strong class="font-semibold">CAAP Status:</strong> {{ user.reviewers.caap }}</p>
            <p><strong class="font-semibold">Purchase Date:</strong> {{ (user.caap_data && user.caap_data.purchase_date) ? moment.unix(user.caap_data.purchase_date/1000).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">GCash Ref. No.:</strong> {{ (user.caap_data && user.caap_data.gcash_refno) ? user.caap_data.gcash_refno : '-' }} <PencilIcon @click="updateGCashRefCode(user.caap_data,'caap_data')" class="w-4 h-4 inline-flex cursor-pointer"/></p>
            <p><strong class="font-semibold">Proof of Payment:</strong> 
              <a v-if="user.caap_data && user.caap_data.proof_image_url" :href="user.caap_data.proof_image_url" target="_blank" rel="noopener noreferrer" class=" ml-1 inline-flex justify-start items-center text-blue-600 underline"> 
                <PaperClipIcon class=" w-4 h-4"/>
                See image
              </a>
              <i v-else class=" text-warm-gray-400 ml-1">No image</i>
            </p>
            
            <p><strong class="font-semibold">Activate Later?</strong> {{ (user.caap_data && user.caap_data.activateLater) ? user.caap_data.activateLater : '-'}}</p>
            <p><strong class="font-semibold">Activation Date:</strong> {{ (user.caap_data && user.caap_data.activation_date) ? moment.unix(user.caap_data.activation_date.seconds).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">Expiration Date:</strong> {{ (user.caap_data && user.caap_data.expiration_date) ? moment.unix(user.caap_data.expiration_date.seconds).format('LLLL') : '-'}}</p>
            <div v-if="user.caap_data && user.caap_data.isBundled" class="pt-2">
              <span class="py-0.5 px-2 rounded-full border bg-warm-gray-200">PURCHASED IN BUNDLE </span>
            </div>

            <button v-if="user.reviewers.caap == 'active' && user.caap_data && user.caap_data.expiration_date" class="underline mt-2 text-purple-600 flex justify-center items-center" @click="openExpirationDateModal(user.caap_data.expiration_date, 'caap')">
              <CalendarDaysIcon class="w-4 h-4"/> UPDATE EXPIRATION DATE
            </button>

            <div class="bg-gray-100 py-3 mt-4 text-xs text-black px-3">
              <code>
                {{ user.caap_data }}
              </code>
            </div>

            <div class="flex justify-start items-center gap-2">

              <button v-if="user.reviewers.caap == 'inactive'" class="underline mt-4 " @click="purchaseForUser(user,'caap')">PURCHASE FOR USER (3 Months)</button>
              <button v-if="user.reviewers.caap == 'inactive'" class="underline mt-4 " @click="purchaseForUser(user,'caap', 6)">PURCHASE FOR USER (6 Months)</button>

              <button v-if="user.reviewers.caap == 'active' || user.reviewers.caap == 'expired'" class="underline mt-4 text-red-600 flex justify-center items-center" @click="revokePurchase(user,'caap')">
                <HandThumbDownIcon class="w-4 h-4"/> REVOKE
              </button>
              <div v-if="user.isCaapBlocked" class=" w-min">
                <router-link :to="{path: '/soft-blocks'}" class="underline mt-4 text-green-600 flex justify-start items-center">
                  <LockOpenIcon class="w-4 h-4"/> UNBLOCKED
                </router-link>
              </div>
            </div>
            

            <h2 class="font-semibold mt-2">Achievements:</h2>
            <ul class="ml-6">
              <li v-for="achievement in user.achievements" :key="achievement" class="list-disc">
                {{ achievement }}
              </li>
            </ul>
            <p class="mt-3 text-gray-500 italic">
              Practice / mock review stats moved to the
              <button type="button" class="underline text-blue-600" @click="activeTab = 'activity'">CAAP review activity</button>
              tab.
            </p>
          </div>

          <!-- CAAP Lite Info -->
          <div class="py-4">
            <h1 class="font-bold text-sm">CAAP Lite Info</h1>
            <p><strong class="font-semibold">CAAP Lite Status:</strong> {{ user.reviewers.caap_lite ? user.reviewers.caap_lite : '-' }}</p>
            <p><strong class="font-semibold">Purchase Date:</strong> {{ (user.caap_lite_data && user.caap_lite_data.purchase_date) ? moment.unix(user.caap_lite_data.purchase_date/1000).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">GCash Ref. No.:</strong> {{ (user.caap_lite_data && user.caap_lite_data.gcash_refno) ? user.caap_lite_data.gcash_refno : '-' }}</p>

            <button v-if="!user.reviewers.caap_lite || user.reviewers.caap_lite == 'inactive'" class="underline mt-4 " @click="purchaseForUser(user, 'caap_lite')">PURCHASE FOR USER</button>
            <button v-if="user.reviewers.caap_lite == 'active'" class="underline mt-4 text-red-600 flex justify-center items-center" @click="revokePurchase(user,'caap_lite')">
              <HandThumbDownIcon class="w-4 h-4"/> REVOKE
            </button>
          </div>

          <!-- CAAP Plus Info -->
          <div class="py-4">
            <h1 class="font-bold text-sm">CAAP Plus Info</h1>
            <p><strong class="font-semibold">CAAP Plus Status:</strong> {{ user.reviewers.caap_plus ? user.reviewers.caap_plus : '-' }}</p>
            <p><strong class="font-semibold">Purchase Date:</strong> {{ (user.caap_plus_data && user.caap_plus_data.purchase_date) ? moment.unix(user.caap_plus_data.purchase_date/1000).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">GCash Ref. No.:</strong> {{ (user.caap_plus_data && user.caap_plus_data.gcash_refno) ? user.caap_plus_data.gcash_refno : '-' }} <PencilIcon @click="updateGCashRefCode(user.caap_plus_data,'caap_plus_data')" class="w-4 h-4 inline-flex cursor-pointer"/></p>
            <p><strong class="font-semibold">Proof of Payment:</strong> 
              <a v-if="user.caap_plus_data && user.caap_plus_data.proof_image_url" :href="user.caap_plus_data.proof_image_url" target="_blank" rel="noopener noreferrer" class=" ml-1 inline-flex justify-start items-center text-blue-600 underline"> 
                <PaperClipIcon class=" w-4 h-4"/>
                See image
              </a>
              <i v-else class=" text-warm-gray-400 ml-1">No image</i>
            </p>

            <p><strong class="font-semibold">Activate Later?</strong> {{ (user.caap_plus_data && user.caap_plus_data.activateLater) ? user.caap_plus_data.activateLater : '-'}}</p>
            <p><strong class="font-semibold">Activation Date:</strong> {{ (user.caap_plus_data && user.caap_plus_data.activation_date) ? moment.unix(user.caap_plus_data.activation_date.seconds).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">Expiration Date:</strong> {{ (user.caap_plus_data && user.caap_plus_data.expiration_date) ? moment.unix(user.caap_plus_data.expiration_date.seconds).format('LLLL') : '-'}}</p>

            <div v-if="user.caap_plus_data && user.caap_plus_data.isBundled" class="pt-2">
              <span class="py-0.5 px-2 rounded-full border bg-warm-gray-200">PURCHASED IN BUNDLE </span>
            </div>

            <button v-if="user.reviewers.caap_plus == 'active' && user.caap_plus_data && user.caap_plus_data.expiration_date" class="underline mt-2 text-purple-600 flex justify-center items-center" @click="openExpirationDateModal(user.caap_plus_data.expiration_date, 'caap_plus')">
              <CalendarDaysIcon class="w-4 h-4"/> UPDATE EXPIRATION DATE
            </button>

            <div class="bg-gray-100 py-3 mt-4 text-xs text-black px-3">
              <code>
                {{ user.caap_plus_data }}
              </code>
            </div>

            <button v-if="(!user.reviewers.caap_plus || user.reviewers.caap_plus == 'inactive')" class="underline mt-4 " @click="purchaseForUser(user, 'caap_plus')">PURCHASE FOR USER</button>
            <button v-if="user.reviewers.caap_plus == 'active'" class="underline mt-4 text-red-600 flex justify-center items-center" @click="revokePurchase(user,'caap_plus')">
              <HandThumbDownIcon class="w-4 h-4"/> REVOKE
            </button>
            <button v-if="user.isPlusBlocked" class="underline mt-4 text-green-600 flex justify-center items-center" @click="unblockedPlus(user)">
              <LockOpenIcon class="w-4 h-4"/> UNBLOCKED and Ext. 3 Months
            </button>
          </div>

          <!-- MTP Info -->
          <div class="py-4">
            <h1 class="font-bold text-sm">MTP Info</h1>
            <p><strong class="font-semibold">MTP Status:</strong> {{ user.reviewers.mtp ? user.reviewers.mtp : '-' }}</p>
            <p><strong class="font-semibold">Purchase Date:</strong> {{ (user.mtp_data && user.mtp_data.purchase_date) ? moment.unix(user.mtp_data.purchase_date/1000).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">GCash Ref. No.:</strong> {{ (user.mtp_data && user.mtp_data.gcash_refno) ? user.mtp_data.gcash_refno : '-' }} <PencilIcon @click="updateGCashRefCode(user.mtp_data,'mtp_data')" class="w-4 h-4 inline-flex cursor-pointer"/></p>
            <p><strong class="font-semibold">Proof of Payment:</strong> 
              <a v-if="user.mtp_data && user.mtp_data.proof_image_url" :href="user.mtp_data.proof_image_url" target="_blank" rel="noopener noreferrer" class=" ml-1 inline-flex justify-start items-center text-blue-600 underline"> 
                <PaperClipIcon class=" w-4 h-4"/>
                See image
              </a>
              <i v-else class=" text-warm-gray-400 ml-1">No image</i>
            </p>

            <p><strong class="font-semibold">Activate Later?</strong> {{ (user.mtp_data && user.mtp_data.activateLater) ? user.mtp_data.activateLater : '-'}}</p>
            <p><strong class="font-semibold">Activation Date:</strong> {{ (user.mtp_data && user.mtp_data.activation_date) ? moment.unix(user.mtp_data.activation_date.seconds).format('LLLL') : '-'}}</p>
            <p><strong class="font-semibold">Expiration Date:</strong> {{ (user.mtp_data && user.mtp_data.expiration_date) ? moment.unix(user.mtp_data.expiration_date.seconds).format('LLLL') : '-'}}</p>

            <button v-if="user.reviewers.mtp == 'active' && user.mtp_data && user.mtp_data.expiration_date" class="underline mt-2 text-purple-600 flex justify-center items-center" @click="openExpirationDateModal(user.mtp_data.expiration_date, 'mtp')">
              <CalendarDaysIcon class="w-4 h-4"/> UPDATE EXPIRATION DATE
            </button>

            <div class="bg-gray-100 py-3 mt-4 text-xs text-black px-3">
              <code>
                {{ user.mtp_data }}
              </code>
            </div>

            <button v-if="(!user.reviewers.mtp || user.reviewers.mtp == 'inactive')" class="underline mt-4 " @click="purchaseForUser(user, 'mtp', 1)">PURCHASE FOR USER</button>
            <button v-if="user.reviewers.mtp == 'active'" class="underline mt-4 text-red-600 flex justify-center items-center" @click="revokePurchase(user,'mtp')">
              <HandThumbDownIcon class="w-4 h-4"/> REVOKE
            </button>
            <button v-if="user.isMtpBlocked" class="underline mt-4 text-green-600 flex justify-center items-center" @click="unblockedMtp(user)">
              <LockOpenIcon class="w-4 h-4"/> UNBLOCKED and Ext. 1 Months
            </button>
          </div>


          <div v-if="user.license" class="py-4">
            <h1 class="font-bold text-sm">License Info</h1>
            <p><strong class="font-semibold">Is Licensed:</strong> {{ user.license.isLicensed }}</p>
            <div v-if="user.license.isLicensed" class="ml-6">
              <ul>
                <li class="list-disc">
                  <p><strong class="font-semibold">Exam Date:</strong> {{ user.license.exam_date }}</p>
                </li>
                <li class="list-disc">
                  <p><strong class="font-semibold">Test Results:</strong> <a :href="user.license.test_results" target="_blank">View Here</a></p>
                </li>
              </ul>
            </div>
            <p><strong class="font-semibold">Is Verified:</strong> {{ user.license.isVerified }}</p>
            <p><strong class="font-semibold">Is Revoked:</strong> {{ user.license.isRevoked }}</p>

            <button v-if="user.license && user.license.isVerified" class="underline mt-4 " @click="revokeLicense(user)">REVOKE LICENSE</button>

          </div>


          <div class="py-4">
            <!-- <h1 class="font-bold text-sm">Others</h1> -->
            <div class="flex gap-3">
              <div class="flex h-6 shrink-0 items-center">
                <div class="group grid size-4 grid-cols-1">
                  <input v-model="user.isNotEligibleToClaimAchievementBox" @change="updateAchievementBoxEligibility(user, user.isNotEligibleToClaimAchievementBox)" id="isNotEligibleToClaimAchievementBox" aria-describedby="isNotEligibleToClaimAchievementBox-description" name="isNotEligibleToClaimAchievementBox" type="checkbox" class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto" />
                  <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                    <path class="opacity-0 group-has-[:checked]:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path class="opacity-0 group-has-[:indeterminate]:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>
              <div class="text-sm/6">
                  <label for="isNotEligibleToClaimAchievementBox" class="font-medium text-gray-900">Ineligible for Achievement Box</label>
                  <p id="isNotEligibleToClaimAchievementBox-description" class="text-gray-500">
                    Add this user to the blacklist. Not eligible to claim the Achievement Box.
                  </p>
              </div>

            </div>
          </div>

          <!-- Promote/Demote to Endorser -->
          <div class="py-4">
            <h1 class="font-bold text-sm">Endorser Management</h1>
            <div class="mt-2">
              <a v-if="user.type == 'endorser'" href="#" @click.prevent="demoteToUser(user)" class="text-xs text-yellow-500 hover:text-yellow-400 flex items-center">
                DEMOTE
                <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </a>
              <a v-else href="#" @click.prevent="promoteToEndorser(user)" class="text-xs text-purple-500 hover:text-purple-400 flex items-center">
                PROMOTE
                <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </a>
            </div>
          </div>

        
        </li>
      </ul>
    </main>


    <!-- Waiting Screen -->
    <main v-if="isInitializing" class="overflow-auto h-full w-full flex justify-center items-center">
        <Logo class="-mt-24 w-24 animate-pulse text-warm-gray-300" />
    </main>

    <!-- Notification -->
    <div v-if="isWaiting" aria-live="assertive" class="absolute pointer-events-none bottom-14 inset-0 flex items-end px-2 py-4">
      <div class="flex w-full flex-col space-y-4 items-center">
        <!--
          Notification panel, dynamically insert this into the live region when it needs to be displayed

          Entering: "transform ease-out duration-300 transition"
            From: "translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            To: "translate-y-0 opacity-100 sm:translate-x-0"
          Leaving: "transition ease-in duration-100"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div class="pointer-events-auto w-auto max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div class="px-6 py-3">
            <div class="flex items-center justify-between">
              <p class="flex-1 text-sm text-center font-medium text-gray-900">{{ notificationMessage }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>


    <EndorserModal 
      :isEndorserModalOpen="isEndorserModalOpen"
      :user="selected_user"
      @closeEndorserModal="closeEndorserModal()"
      @updateUser="updateUser()" 
    />

     <!-- Update Expiration Date Modal -->
  <TransitionRoot appear :show="isExpirationDateModalOpen" as="template">
    <Dialog as="div" @close="closeExpirationDateModal">
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">

          <DialogOverlay class="fixed inset-0 bg-warm-gray-800 opacity-40 dark:bg-warm-gray-400" />

          <span class="inline-block h-screen align-middle" aria-hidden="true">
            &#8203;
          </span>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
            >
            <div
              class="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >

              <div class="flex-row items-start text-warm-gray-700 p-6">

                <div class="w-full">
                  <label for="email">Expiration date:</label>
                  <input type="date" v-model="expirationDateField" class="w-full h-10 p-2 rounded border bg-warm-gray-50 focus:outline-none focus:border-gray-500 mt-2">
                  <!-- <input type="email" id="email" class="w-full h-10 p-2 rounded border bg-warm-gray-50 focus:outline-none focus:border-yellow-500 mt-2 dark:text-warm-gray-800" placeholder="Enter email address" /> -->
                  <!-- <p  v-if="isGettingUid" class="text-yellow-600 flex justify-start items-center">
                    <SpinningIcon class="h-4 w-4 mr-1" />
                    <small>Checking for email... </small>
                  </p> -->
                  <!-- <p v-if="!isGettingUid && addForm.uid" class=" text-sm dark:text-yellow-600 text-yellow-700">User found!</p> -->
                </div>

                <div class="mt-2 flex justify-end w-full gap-2">
                  <button
                    type="button"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-warm-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    @click="closeExpirationDateModal"
                    >
                    Cancel
                  </button>

                  <button
                    type="button"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-warm-gray-700 bg-purple-300 border border-transparent rounded-md hover:bg-purple-400 focus:bg-purple-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    @click="saveExpirationDate()"
                    >
                    Update
                  </button>

                </div>

              </div>

              <!-- <div class="border-t p-6 pt-3 flex justify-between dark:border-warm-gray-600">
                <div>
                 

                  <button
                    type="button"
                    :class=" [ !addForm.uid || isAdding ? 'disabled:cursor-not-allowed text-warm-gray-300 bg-red-300' : 'text-warm-gray-100 bg-red-600 hover:bg-red-800 focus:bg-red-800 dark:bg-red-700 dark:hover:bg-red-600 dark:focus:bg-red-600 dark:text-warm-gray-200' , 'ml-2 inline-flex justify-center px-4 py-2 text-sm font-medium border border-transparent rounded-md  focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500']"
                    @click="block"
                    :disabled="!addForm.uid || isAdding"
                    >
                    <SpinningIcon class="h-4 w-4 mr-1" v-if="isUpdating" />
                    Update
                  </button>
                </div>
              </div> -->
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  

</template>
<script setup>

import ROUTER from "@/router";

import { ref, computed, reactive } from "vue";
import moment from "moment";
import { getDocs, getDoc, collection, orderBy, query, doc, updateDoc, setDoc, deleteDoc, where, limit, serverTimestamp } from "firebase/firestore";
import { auth, db, functions } from '@/firebase/config.js';
import { httpsCallable } from "firebase/functions";
import { getAuth, getIdTokenResult } from 'firebase/auth';


import {
  // modal
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from '@headlessui/vue';

import Logo from "@/_for_dash/icons/Logo";
import EndorserModal from "@/_for_dash/modals/EndorserModal";
import { HandThumbDownIcon, CalendarDaysIcon, LockOpenIcon, PaperClipIcon, PencilIcon } from "@heroicons/vue/24/outline";

const lastPractices = ref([]);
const lastExams = ref([]);
const allPractices = ref([]);
const allMocks = ref([]);
const subtopicToSubject = ref({});
const isLoadingReviewActivity = ref(false);
const activeTab = ref('account');
const expandedSubjects = reactive({});

const RECENT_LIMIT = 3;
const ACTIVITY_FETCH_LIMIT = 100;

const isInitializing = ref(true)
const user = ref(null);
const schools = ref([]);
const isWaiting = ref(false);
const notificationMessage = ref('');

/**
 * Format a slug id for display: aircraft-maintenance → Aircraft Maintenance
 */
const formatSlugLabel = (id) => {
  if (!id) return '—'
  return String(id)
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatScore = (score) => {
  if (score == null || Number.isNaN(Number(score))) return null
  const n = Number(score)
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

const formatActivityDate = (timeStart) => {
  if (timeStart == null) return '—'
  const ms = typeof timeStart === 'number' ? timeStart : Number(timeStart)
  if (!ms || Number.isNaN(ms)) return '—'
  return moment(ms).format('MMM D, YYYY h:mm A')
}

/**
 * Derive subject id from a practice doc:
 * 1) url path /caap/course/{course}/{subject}/{subtopic}/practice
 * 2) subtopic → subjects[] map from CAAP subtopics
 */
const getSubjectIdFromPractice = (data) => {
  const url = data?.url
  if (typeof url === 'string') {
    const match = url.match(/\/caap\/course\/[^/]+\/([^/]+)\/([^/]+)\/practice/)
    if (match && match[1]) return match[1]
  }
  const subtopicId = data?.subtopic
  if (subtopicId && subtopicToSubject.value[subtopicId]) {
    return subtopicToSubject.value[subtopicId]
  }
  return null
}

const normalizePractice = (id, data) => {
  const subjectId = getSubjectIdFromPractice(data)
  const subtopicId = data?.subtopic || null
  return {
    id,
    subjectId,
    subtopicId,
    subjectLabel: formatSlugLabel(subjectId),
    subtopicLabel: formatSlugLabel(subtopicId),
    dateLabel: formatActivityDate(data?.time?.start),
    score: data?.score ?? null,
    result: data?.result || null,
    status: data?.status || null,
    timeStart: data?.time?.start ?? 0
  }
}

const normalizeMock = (id, data) => {
  const subjectId = data?.subject || null
  return {
    id,
    subjectId,
    subjectLabel: formatSlugLabel(subjectId),
    dateLabel: formatActivityDate(data?.time?.start),
    score: data?.score ?? null,
    result: data?.result || null,
    status: data?.status || null,
    timeStart: data?.time?.start ?? 0
  }
}

const subjectsWithActivity = computed(() => {
  const map = {}

  const ensure = (subjectId) => {
    if (!subjectId) return null
    if (!map[subjectId]) {
      map[subjectId] = {
        subjectId,
        subjectLabel: formatSlugLabel(subjectId),
        practices: [],
        mocks: []
      }
    }
    return map[subjectId]
  }

  allPractices.value.forEach((p) => {
    const group = ensure(p.subjectId)
    if (group && group.practices.length < RECENT_LIMIT) {
      group.practices.push(p)
    }
  })

  allMocks.value.forEach((m) => {
    const group = ensure(m.subjectId)
    if (group && group.mocks.length < RECENT_LIMIT) {
      group.mocks.push(m)
    }
  })

  // Include subjects that only appear on user.subjects (stats) even with no recent activity
  if (user.value?.subjects) {
    user.value.subjects.forEach((s) => {
      ensure(s.id)
    })
  }

  return Object.values(map).sort((a, b) => a.subjectLabel.localeCompare(b.subjectLabel))
})

const toggleSubjectPanel = (subjectId) => {
  expandedSubjects[subjectId] = !expandedSubjects[subjectId]
}
const isExpirationDateModalOpen = ref(false);
const expirationDateField = ref(null);
const revTypeToUpdateForDateExp = ref(null);

// Endorser modal state
const isEndorserModalOpen = ref(false);
const selected_user = ref(null);

const openExpirationDateModal = (expDate, rev)=>{
  const dateFromSeconds = new Date(expDate.seconds * 1000);
  const dateString = dateFromSeconds.toISOString().slice(0, 10);
  expirationDateField.value = dateString;
  isExpirationDateModalOpen.value = true;
  revTypeToUpdateForDateExp.value = rev;
}

const updateGCashRefCode = async (data, keyName) => {

  const input = prompt("Enter GCash ref. code:", data.gcash_refno || "");

  if (input === null) {
    // User canceled
    return;
  }

  if (!/^\d{13}$/.test(input)) {
    alert("Invalid input. Please enter a 13-digit number.");
    return;
  }

  isWaiting.value = true;

  try {
    await updateDoc(doc(db, "users", user.value.id), {
      [`${keyName}.gcash_refno`]: input,
    });

    notificationMessage.value = '✅ GCash ref. code updated.';
    setTimeout(() => {
        data.gcash_refno = input; // update local copy
        isWaiting.value = false;
    }, 1000);

  } catch (error) {
    console.error("Error updating GCash ref code:", error);
    alert("Failed to update. Please try again.");
  }
};


const saveExpirationDate = async () => { 

  isExpirationDateModalOpen.value = false;

  const expDate = new Date(expirationDateField.value);

  isWaiting.value = true;
  notificationMessage.value = `Updating User\'s ${revTypeToUpdateForDateExp.value.toUpperCase()} expiration date...`;

  let _newExpDate = null;
  let _newExpDateDB = null;
  switch (revTypeToUpdateForDateExp.value) {
    case 'caap':
        _newExpDate = {caapNewExpAt: expDate}
        _newExpDateDB = {['caap_data.expiration_date']: expDate}
      break;
    case 'caap_plus':
        _newExpDate = {plusNewExpAt: expDate}
        _newExpDateDB = {['caap_plus_data.expiration_date']: expDate}
      break;
    case 'mtp':
        _newExpDate = {mtpNewExpAt: expDate}
        _newExpDateDB = {['mtp_data.expiration_date']: expDate}
      break;
  }

  const updateExpirationDateFbFunc = httpsCallable(functions, "updateReviewerExpirationDate");
  const updateExpirationDate = await updateExpirationDateFbFunc({
    email: user.value.email,
    ..._newExpDate
  })

  
  if(updateExpirationDate.data.success){

    console.log('User claims has been updated successfully.')

    updateDoc( doc(db, "users", user.value.id), _newExpDateDB)
      .then( async () => { 
       
        notificationMessage.value = '✅ Expiration date has been updated.';

        setTimeout(() => {
          isWaiting.value = false;
          initialize();
        }, 2000);


      })
      .catch((error) => { 
        console.error(`Error updating user ${revTypeToUpdateForDateExp.value.toUpperCase()} expiration date: `, error);
      });


  }else{
    console.error(`An error happened during updating ${revTypeToUpdateForDateExp.value.toUpperCase()} expiration date.`)
  }

}

const closeExpirationDateModal = ()=>{
  isExpirationDateModalOpen.value = false;
}

const fetchUser = async (uid) => {
  return new Promise(async (resolve, reject) => {

    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Document data:", docSnap.data());
      let data = docSnap.data()
      data.id = docSnap.id;
      console.log({data})
      resolve(data)
    } else {
      // doc.data() will be undefined in this case
      console.error("No such document!")
    }
  })
}

const fetchAllSchools = async () => {
  return new Promise(async (resolve, reject) => {
    getDocs( query(collection(db, "schools")))
      .then((querySnapshot) => { 
        if(querySnapshot.size > 0){
          var _schools = [];
          querySnapshot.forEach((doc) => {
            let data = doc.data()
            data.id = doc.id
            _schools.push(data)
          });
          resolve(_schools)
        }else{
          console.log("Query returned no results!")
        }
      })
      .catch((error) => {
        console.error("Error retreiving schools list from DB");
        throw `Error getting user document: ${error}`;
      });
  })
}


/**
 * Purchase CAAP reviewer for the user
 */
 const purchaseForUser = async (user, reviewer, monthDuration = 3) => {


  // ask current price
  let currentPrice;
  currentPrice = prompt(`Enter current price for ${monthDuration} Month/s duration:`, monthDuration == 3 ? 3999 : 5999);
  
  if (!currentPrice || isNaN(currentPrice)) {
    // The input is not a number
    console.log('Input is not a number or is not a valid number:', currentPrice);
    return;
  }

  isWaiting.value = true;
  notificationMessage.value = `Purchasing ${reviewer.toUpperCase()} for user...`;

  /**
   * Initialize default values for the data
   * @todo @junrey refactor this so that it will be define by user
   * ENG-34: purchase_date_v2 is server time. Legacy purchase_date string is
   * stamped from that Timestamp after the write commits.
   */
  let _data = {
    purchase_date_v2: serverTimestamp(),
    gcash_refno: 'PURCHASED_VIA_ADMIN',
    finalPrice: currentPrice,
    activateLater: true,
  }

  switch (monthDuration) {
    case 1:
      _data.duration = 30;
      _data.durationInWords = '1 Month';
      break;
    case 3:
      _data.duration = 90;
      _data.durationInWords = '3 Months';
      break;
    case 6:
      _data.duration = 180;
      _data.durationInWords = '6 Months';
      break;
   
  }

  let _reviewers;
  let dataToUpdate;

  switch (reviewer) {
    case 'caap':
      _reviewers = {
        'reviewers.caap': 'paid'
      }  
      dataToUpdate = {caap_data: _data}
      break;
    case 'caap_lite':
      _reviewers = {
        'reviewers.caap_lite': 'paid'
      }  
      dataToUpdate = {caap_lite_data: _data}
      break;
    case 'caap_plus':
      _reviewers = {
        'reviewers.caap_plus': 'paid'
      }  
      dataToUpdate = {caap_plus_data: _data}
      break;
    case 'mtp':
      _reviewers = {
        'reviewers.mtp': 'paid'
      }  
      dataToUpdate = {mtp_data: _data}
      break;
  }

  const dataKey = Object.keys(dataToUpdate)[0];
  const userRef = doc(db, "users", user.id);

  try {
    await updateDoc(userRef, {
      ..._reviewers,
      ...dataToUpdate
    });

    const snap = await getDoc(userRef);
    const committedPurchaseDate = snap.exists() ? snap.data()?.[dataKey]?.purchase_date_v2 : null;
    if (committedPurchaseDate && typeof committedPurchaseDate.toMillis === 'function') {
      await updateDoc(userRef, {
        [`${dataKey}.purchase_date`]: String(committedPurchaseDate.toMillis())
      });
    }

    notificationMessage.value = `✅ User has now purchased ${reviewer.toUpperCase()}.`;

    setTimeout(() => {
      isWaiting.value = false;
      initialize();
    }, 2000);
  } catch (error) {
    console.error("Error updating user data: ", error);
    isWaiting.value = false;
  }

}

const revokePurchase = async (user,rev) => {
  
  isWaiting.value = true;
  notificationMessage.value = `Revoking User\'s ${rev.toUpperCase()} access...`;

  //identify which reviewer to revoke and then initialize the payload to send to firebase function
  let funcPayload = {};
  let keysToremove = [];
  switch (rev) {
    case 'caap':
        funcPayload = {caap: false};
        keysToremove.push('caapExpAt');
      break;
    case 'caap_lite':
        funcPayload = {caap_lite: false};
        break;
    case 'caap_plus':
          funcPayload = {caap_plus: false};
          keysToremove.push('plusExpAt');
      break;
    case 'mtp':
        funcPayload = {mtp: false};
        keysToremove.push('mtpExpAt');
      break;
  }

  const updateUserClaimsFbFunc = httpsCallable(functions, "updateUserClaims");
  const updateUserClaimsResponse = await updateUserClaimsFbFunc({
    email: user.email,
    payload: funcPayload,
    toRemoveKeys: keysToremove,
  })

  if(updateUserClaimsResponse.data.success){

    let _data;
    let _reviewers;

    switch (rev) {
      case 'caap':
          _data = {caap_data : {
            purchase_date: '',
            gcash_refno: ''
          }}
    
          _reviewers = {
            'reviewers.caap': 'inactive'
          }  
        break;
      case 'caap_lite':
          _data = {caap_lite_data : {
            purchase_date: '',
            gcash_refno: ''
          }}
      
          _reviewers = {
            'reviewers.caap_lite': 'inactive'
          } 
        break;
      case 'caap_plus':
          _data = {caap_plus_data : {
            purchase_date: '',
            gcash_refno: ''
          }}
    
          _reviewers = {
            'reviewers.caap_plus': 'inactive'
          } 
        break;
      case 'mtp':
          _data = {mtp_data : {
            purchase_date: '',
            gcash_refno: ''
          }}

          _reviewers = {
            'reviewers.mtp': 'inactive'
          } 

        break;
    }

    notificationMessage.value = `Updating User ${rev.toUpperCase()} data...`;
    updateDoc( doc(db, "users", user.id), { ..._reviewers, ..._data })
      .then(() => { 
        console.log(`User's ${rev.toUpperCase()} information has been successfully updated.`) 
        
        notificationMessage.value = '✅ Purchase has been revoke.';
        
        setTimeout(() => {
          isWaiting.value = false;
          initialize();
        }, 2000);

      })
      .catch((error) => { 
        console.error(`Error updating user ${rev.toUpperCase()} information: `, error);
      });


    }else{
      console.error('An error happened during CAAP reviewer revoke.')
    }

}

const updateAchievementBoxEligibility = async (user, val) => {
  
  isWaiting.value = true;
  notificationMessage.value = `Update Achievement Box Eligibility...`;
  updateDoc( doc(db, "users", user.id), { isNotEligibleToClaimAchievementBox: val })
    .then(() => { 
      
      notificationMessage.value = '✅ Achievement Box Eligibility updated.';
      setTimeout(() => {
        isWaiting.value = false;
        initialize();
      }, 2000);

    })
    .catch((error) => { 
      console.error(`Error updating achievement box eligibilty: `, error);
    });


}

const revokeLicense = async (user) => {

  isWaiting.value = true;
  notificationMessage.value = 'Confirming license information...';

  console.log("User's license information will now be updated.") 

  let _license = user.license;
  _license.isVerified = false;
  _license.isRevoked = true;

  let _skillpoints = user.skillpoints - 2000;

  /**
   * Update User
   */
  updateDoc( doc(db, "users", user.id), { 
        license: _license,
        skillpoints: _skillpoints
      })
      .then(async () => { 
        console.log("User's license information has been successfully updated.") 
        
        notificationMessage.value = 'Upating leaderboard points...';
        
        /**
         * 
         * Global Leaderboard +2000
         * 
         */

        let user_global_leaderboard = await fetchUserInGlobalLeaderboard(user.id)
        let _global_skillpoints = user_global_leaderboard.skillpoints - 2000;
        updateDoc( doc(db, "leaderboard", user.id), { 
              skillpoints: _global_skillpoints,
              isVerified: false
            })
            .then(async () => { 
              console.log("User's global skillpoints has been successfully updated.") 
              
              notificationMessage.value = 'Upating season2 leaderboard points...';
              
              /**
              * 
              * S2 Leaderboard +2000
              * 
              */

              let user_s2_leaderboard = await fetchUserInS2Leaderboard(user.id)
              let _s2_skillpoints = user_s2_leaderboard.skillpoints - 2000;
              updateDoc( doc(db, "s2leaderboard", user.id), { 
                    skillpoints: _s2_skillpoints,
                    isVerified: false
                  })
                  .then(() => { 
                    console.log("User's s2 skillpoints has been successfully updated.") 
                    
                    notificationMessage.value = '✅ License is now revoked.';
                    
                    setTimeout(() => {
                      isWaiting.value = false;
                      initialize();
                    }, 2000);

                  })
                  .catch((error) => { 
                    console.error("Error updating user license information: ", error);
                  });

            })
            .catch((error) => { 
              console.error("Error updating user license information: ", error);
            });

      })
      .catch((error) => { 
        console.error("Error updating user license information: ", error);
      });

}

const fetchUserInS2Leaderboard = async (uid) => {
  return new Promise(async (resolve, reject) => {

    const docRef = doc(db, "s2leaderboard", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      let data = docSnap.data()
      data.id = docSnap.id;
      resolve(data)
    } else {
      // doc.data() will be undefined in this case
      console.error("No such document!")
    }
  })
}

const fetchUserInGlobalLeaderboard = async (uid) => {
  return new Promise(async (resolve, reject) => {

    const docRef = doc(db, "leaderboard", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      let data = docSnap.data()
      data.id = docSnap.id;
      resolve(data)
    } else {
      // doc.data() will be undefined in this case
      console.error("No such document!")
    }
  })
}

const fetchUserClaims = async(email) => {
  const fetchUserCustomClaimsFbFunc = httpsCallable(functions, "fetchUserCustomClaims");
  const userCustomClaims = await fetchUserCustomClaimsFbFunc({email})
  console.log(userCustomClaims.data)
  alert('Check console log!')
}

const initialize = async () => {

  isInitializing.value = true;
  if(ROUTER.currentRoute.value.params.id){
    let uid = ROUTER.currentRoute.value.params.id;
    user.value = await fetchUser(uid);
    schools.value = await fetchAllSchools();  

    // Load review activity in background so account tab can show first
    loadCaapReviewActivity();

    isInitializing.value = false;
  }else{
    console.log('No ID is passed.')
  }


}

const loadSubtopicSubjectMap = async () => {
  try {
    const snap = await getDocs(collection(db, 'reviewers', 'caap', 'subtopics'))
    const map = {}
    snap.forEach((subDoc) => {
      const data = subDoc.data() || {}
      const subjects = Array.isArray(data.subjects) ? data.subjects : []
      // Prefer first subject; most subtopics belong to one
      if (subjects.length > 0) {
        map[subDoc.id] = subjects[0]
      }
    })
    subtopicToSubject.value = map
  } catch (error) {
    console.error('Error loading CAAP subtopics map:', error)
    subtopicToSubject.value = {}
  }
}

const loadCaapReviewActivity = async () => {
  if (!user.value?.id) return

  isLoadingReviewActivity.value = true
  lastPractices.value = []
  lastExams.value = []
  allPractices.value = []
  allMocks.value = []

  try {
    await loadSubtopicSubjectMap()

    const [practiceSnap, mockSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, 'practice'),
          where('user', '==', user.value.id),
          orderBy('time.start', 'desc'),
          limit(ACTIVITY_FETCH_LIMIT)
        )
      ),
      getDocs(
        query(
          collection(db, 'mock'),
          where('user', '==', user.value.id),
          orderBy('time.start', 'desc'),
          limit(ACTIVITY_FETCH_LIMIT)
        )
      )
    ])

    const practices = []
    practiceSnap.forEach((practiceDoc) => {
      practices.push(normalizePractice(practiceDoc.id, practiceDoc.data()))
    })

    const mocks = []
    mockSnap.forEach((mockDoc) => {
      mocks.push(normalizeMock(mockDoc.id, mockDoc.data()))
    })

    allPractices.value = practices
    allMocks.value = mocks
    lastPractices.value = practices.slice(0, RECENT_LIMIT)
    lastExams.value = mocks.slice(0, RECENT_LIMIT)
  } catch (error) {
    console.error('Error loading CAAP review activity:', error)
  } finally {
    isLoadingReviewActivity.value = false
  }
}

const unblockedMtp = async (user) => {
  
  isWaiting.value = true;
  notificationMessage.value = `Unblocking Mtp reviewer. Extending to 3 months...`;

  const getReviewerExpiry = (days = 30) => {
      let durationInDays = days;
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + durationInDays);
      return currentDate
  }

  const expDate = getReviewerExpiry();

  // update user claims
  notificationMessage.value = `Updating User Claims...`;
  const softBlockUserRemoveFunc = httpsCallable(functions, "softBlockUserRemoveMtp");
  const softBlockUserRemoveFuncRes = await softBlockUserRemoveFunc({ email: user.email, mtpExpAt: expDate })

  if (softBlockUserRemoveFuncRes.data.success) {

    notificationMessage.value = `Updating User MTP data...`;
    updateDoc( doc(db, "users", user.id), {
            'reviewers.mtp': 'active',
            'isMtpBlocked': false,
            'mtp_data.expiration_date': expDate
        })
      .then( async () => { 

        console.log(`User's MTP information has been successfully updated.`) 
        notificationMessage.value = '✅ MTP has been unblocked and extended to 3 months.';

        setTimeout(() => {
          isWaiting.value = false;
          initialize();
        }, 2000);


      })
      .catch((error) => { 
        console.error(`Error updating user MTP information: `, error);
        isWaiting.value = false;
        initialize();
      });
      
  } else {
        console.error(`Error updating user claims information`);
        isWaiting.value = false;
        initialize();
  }

  

}

const unblockedPlus = async (user) => {
  
  isWaiting.value = true;
  notificationMessage.value = `Unblocking CAAP Plus reviewer. Extending to 3 months...`;

  const getReviewerExpiry = (days = 90) => {
      let durationInDays = days;
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + durationInDays);
      return currentDate
  }

  const expDate = getReviewerExpiry();

  // update user claims
  notificationMessage.value = `Updating User Claims...`;
  const softBlockUserRemoveFunc = httpsCallable(functions, "softBlockUserRemovePlus");
  const softBlockUserRemoveFuncRes = await softBlockUserRemoveFunc({ email: user.email, plusExpAt: expDate })

  if (softBlockUserRemoveFuncRes.data.success) {

    notificationMessage.value = `Updating User CAAP Plus data...`;
    updateDoc( doc(db, "users", user.id), {
            'reviewers.caap_plus': 'active',
            'isPlusBlocked': false,
            'caap_plus_data.expiration_date': expDate
        })
      .then( async () => { 

        console.log(`User's CAAP Plus information has been successfully updated.`) 
        notificationMessage.value = '✅ CAAP Plus has been unblocked and extended to 3 months.';

        setTimeout(() => {
          isWaiting.value = false;
          initialize();
        }, 2000);


      })
      .catch((error) => { 
        console.error(`Error updating user CAAP Plus information: `, error);
        isWaiting.value = false;
        initialize();
      });
      
  } else {
        console.error(`Error updating user claims information`);
        isWaiting.value = false;
        initialize();
  }

  

}

//########## Endorser functions

const demoteToUser = (user) => {
  console.log("Demoting user:");
  console.log(user.id);
  console.log(user.email);
  console.log(user.endorser_code);

  isWaiting.value = true;
  notificationMessage.value = 'Demoting user to regular user...';

  // Update User Data
  updateDoc(doc(db, "users", user.id), { type: "user", endorser_code: "" })
    .then(() => { 
      console.log("Successfully updated user");
      notificationMessage.value = '✅ User has been demoted.';
      setTimeout(() => {
        isWaiting.value = false;
        initialize();
      }, 2000);
    })
    .catch((error) => {  
      console.error("Error updating user data: ", error);
      isWaiting.value = false;
    });

  // Update Endorser Codes
  deleteDoc(doc(db, "_endorser_codes", user.id))
    .then(() => { 
      console.log("Successfully deleted endorser codes") 
    })
    .catch((error) => { 
      console.error("Error deleted endorser codes: ", error)  
    });
};

const promoteToEndorser = (user) => {
  console.log("Promoting user to endorser: ", user);
  selected_user.value = user;
  isEndorserModalOpen.value = true;
};

const closeEndorserModal = () => {
  isEndorserModalOpen.value = false;
  selected_user.value = null;
};

const updateUser = async () => {
  console.log('updating user');
  
  if (!selected_user.value) return;
  
  isWaiting.value = true;
  notificationMessage.value = 'Promoting user to endorser...';
  
  console.log(selected_user.value.id);
  console.log(selected_user.value.email);
  console.log(selected_user.value.endorser_code);

  // Update User Data
  updateDoc(doc(db, "users", selected_user.value.id), { 
    type: "endorser", 
    endorser_code: selected_user.value.endorser_code 
  })
    .then(() => { 
      console.log("Successfully updated user");
      notificationMessage.value = '✅ User has been promoted to endorser.';
      isEndorserModalOpen.value = false;
      setTimeout(() => {
        isWaiting.value = false;
        initialize();
      }, 2000);
    })
    .catch((error) => { 
      console.error("Error updating user data: ", error);
      isWaiting.value = false;
    });

  // Update Endorser Codes
  setDoc(doc(db, "_endorser_codes", selected_user.value.id), { 
    code: selected_user.value.endorser_code 
  })
    .then(() => { 
      console.log("Successfully updated endorser codes") 
    })
    .catch((error) => { 
      console.error("Error updating endorser codes: ", error)  
    });
};

initialize();

</script>
<style>
.h-header{
  min-height: 45pt;
}
</style>